<div class="p-8">
  <!-- Header -->
  <div class="flex flex-col xl:flex-row items-start justify-between mb-4 lg:mb-8">
    <div>
      <h1 class="text-sm font-bold text-navy-600">Categories & Taxonomies</h1>
      <p class="text-xs text-slate-500 mt-1">
        Maintain primary incident classifications and program-specific frameworks.
      </p>
    </div>
    <div
      class="flex w-full mt-4 xl:mt-0 lg:w-auto items-center bg-slate-100 rounded-3xl px-1 py-2 gap-1 h-11"
    >
      <button
        type="button"
        (click)="selectTab('categories')"
        [ngClass]="
          activeTab() === 'categories'
            ? 'bg-white text-navy-900 shadow-sm'
            : 'text-slate-500 hover:text-navy-700'
        "
        class="flex-1 lg:flex-none px-4 py-2.5 rounded-full text-xs transition-all cursor-pointer text-center"
      >
        Category Library
      </button>
      <button
        type="button"
        (click)="selectTab('taxonomies')"
        [ngClass]="
          activeTab() === 'taxonomies'
            ? 'bg-white text-navy-900 shadow-sm'
            : 'text-slate-500 hover:text-navy-700'
        "
        class="flex-1 lg:flex-none px-4 py-2.5 rounded-full text-xs transition-all cursor-pointer text-center"
      >
        Taxonomy Management
      </button>
    </div>
  </div>

  <!-- Search + Create -->
  <div
    class="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-3 mb-6"
  >
    <div class="relative w-full sm:max-w-xs">
      <img
        src="/icons/search-icon.svg"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
        alt=""
      />
      <input
        type="text"
        placeholder="Filter by Name"
        [value]="searchQuery()"
        (input)="onSearchInput($event)"
        class="w-full pl-9 pr-4"
      />
    </div>
    <button
      type="button"
      (click)="
        activeTab() === 'categories' ? showCreateModal.set(true) : showCreateTaxonomyModal.set(true)
      "
      class="flex items-center justify-center gap-2 bg-accent-500 hover:opacity-90 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-opacity cursor-pointer sm:shrink-0"
    >
      <img src="/icons/plus-white-icon.svg" class="w-4 h-4" alt="" />
      {{ activeTab() === 'categories' ? 'Create Category' : 'Create Taxonomy' }}
    </button>
  </div>

  <!-- Table -->
  <div class="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
    @if (activeTab() === 'categories') {
      <app-data-table
        [columns]="categoryColumns()"
        [rows]="categories()"
        [total]="totalCat()"
        [page]="pageIndex() + 1"
        [pageSize]="pageSize"
        [loading]="loading()"
        [clickableRows]="true"
        (pageChange)="onPageChange($event)"
        (rowAction)="onRowAction($event)"
      />
    } @else {
      <app-data-table
        [columns]="taxonomyColumns()"
        [rows]="taxonomies()"
        [total]="totalTax()"
        [page]="pageIndex() + 1"
        [pageSize]="pageSize"
        [loading]="loading()"
        [clickableRows]="true"
        (pageChange)="onPageChange($event)"
        (rowAction)="onRowAction($event)"
      />
    }
  </div>
</div>

@if (showCreateModal()) {
  <app-create-category-modal
    (closed)="showCreateModal.set(false)"
    (saved)="showCreateModal.set(false); load()"
  />
}

@if (showCreateTaxonomyModal()) {
  <app-create-taxonomy-modal
    (closed)="showCreateTaxonomyModal.set(false)"
    (saved)="showCreateTaxonomyModal.set(false); load()"
  />
}

@if (editingCategory()) {
  <app-edit-category-modal
    [category]="editingCategory()!"
    (closed)="editingCategory.set(null)"
    (saved)="editingCategory.set(null); load()"
    (removed)="editingCategory.set(null); load()"
  />
}

<!-- Category templates -->
<ng-template #nameTpl let-row>
  <div class="flex items-center gap-2.5">
    <img src="/icons/tag-icon.svg" alt="" class="w-4 h-4 shrink-0" />
    <span class="font-medium text-navy-900">{{ row.categoryName }}</span>
  </div>
</ng-template>

<ng-template #typeTpl let-row>
  <span
    class="inline-block px-2.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide border"
    [ngClass]="{
      'border-blue-100 bg-surface-25 text-blue-600':
        row.classificationType === ClassificationType.Situation,
      'border-purple-50 bg-purple-10 text-purple-500':
        row.classificationType === ClassificationType.Response,
    }"
  >
    {{ row.classificationType === ClassificationType.Situation ? 'Situation' : 'Response' }}
  </span>
</ng-template>

<!-- Taxonomy templates -->
<ng-template #taxNameTpl let-row>
  <div class="flex items-center gap-2.5">
    <img src="/icons/folder-icon.svg" alt="" class="w-4 h-4 shrink-0" />
    <span class="font-medium text-navy-900">{{ row.taxonomyName }}</span>
  </div>
</ng-template>

<ng-template #taxTypeTpl let-row>
  <span
    class="inline-block px-2.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide border"
    [ngClass]="{
      'border-accent-500 text-accent-500': row.classificationType === ClassificationType.Situation,
      'border-purple-500 text-purple-500': row.classificationType === ClassificationType.Response,
    }"
  >
    {{ row.classificationType === ClassificationType.Situation ? 'Situation' : 'Response' }}
  </span>
</ng-template>

<ng-template #electionTypeTpl let-row>
  <span class="text-xs text-navy-900">{{
    row.isMultiItem ? 'Multiple Choice' : 'Single Choice'
  }}</span>
</ng-template>

<ng-template #assignedCatTpl let-row>
  <span class="text-xs text-slate-600">
    <span class="font-bold text-navy-900">{{ row.assignedCategories }}</span> Categories
  </span>
</ng-template>
